import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { TranslateModule } from "@ngx-translate/core";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { COUNTRY_OPTIONS } from "../../../../constants/countryOptions";
import { DropdownOption } from "../../../../models/ui/dropdownOption";
import { Tenant } from "../../../../models/api/tenant";
import { TextareaModule } from "@openng/optimus-ui/textarea";
import { SelectModule } from "@openng/optimus-ui/select";

@Component({
  standalone: true,
  selector: "app-tenant-form",
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    TranslateModule,
    InputNumberModule,
    SelectModule,
  ],
  templateUrl: "./tenant-form.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./tenant-form.component.scss",
})
export class TenantFormComponent {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private tenant: Tenant = this.config.data.tenant;

  public form: FormGroup = new FormGroup({
    label: new FormControl(this.tenant.label, Validators.required),
    address: new FormGroup({
      street: new FormControl(this.tenant?.address?.street, Validators.required),
      number: new FormControl(this.tenant?.address?.number, Validators.required),
      zipCode: new FormControl(this.tenant?.address?.zipCode, Validators.required),
      city: new FormControl(this.tenant?.address?.city, Validators.required),
      country: new FormControl(this.tenant?.address?.country, Validators.required),
    }),
    noCoreLimit: new FormControl(this.tenant?.noCoreLimit),
    phoneNumber: new FormControl(this.tenant?.phoneNumber),
    emailAddress: new FormControl(this.tenant?.email),
    notes: new FormControl(this.tenant?.notes),
  });

  public countries: DropdownOption[] = COUNTRY_OPTIONS;

  /**
   * Saves the form data if it is valid.
   * If the form is valid, it updates the tenant properties with the form values
   * and closes the dialog with the updated tenant.
   * If the form is not valid, it simply closes the dialog.
   *
   * @return {void} No return value.
   */
  public save(): void {
    if (this.form?.valid) {
      const tenantFormValues = this.form?.value;

      if (tenantFormValues) {
        this.tenant.properties = tenantFormValues;
        this.ref.close(this.tenant);
      } else {
        this.ref.close();
      }
    }
  }
  /**
   * Closes the dialog.
   *
   * @return {void} No return value.
   */
  public closeDialog(): void {
    this.ref.close();
  }
}
